/**
 * Keeping `?guest_session_token=` out of the address bar, where every analytics
 * tag on the page can read it.
 *
 * The guest session token is a 14-day credential: it reads the order's name,
 * address, phone, email and presigned photo URLs, and it can change the cart.
 * It rides in the query string of every checkout page — Stripe's success URL,
 * the COD, delivery and photo-options steps, the links in our own emails.
 *
 * Scrubbing what WE hand to analytics (analytics-url.ts) was not enough,
 * because the tags do not ask us for the URL:
 *
 *   - the Meta Pixel reads `location.href` itself, on every event, and sends it
 *     as `dl`;
 *   - GA4 records `page_location` from `document.location` and ignores the
 *     scrubbed `page_path` it is given;
 *   - Microsoft Clarity records every URL the tab visits, re-reading
 *     `location.href` after each pushState/replaceState.
 *
 * There is no supported way to hand any of them a different URL, so the only
 * fix is that the token is never in `window.location` while they are around.
 * Like the credit guard (credit-url-guard.ts), this source is inlined as a plain
 * `<script>` at the top of `<head>`, before any tag exists, and it:
 *
 *   1. takes the token out of the URL the page was loaded with, and
 *   2. wraps history.pushState/replaceState, so a client-side navigation to a
 *      URL carrying the token (the checkout steps router.push one) writes the
 *      address bar without it. Next's own wrapper and Clarity's wrap OURS — they
 *      are installed later — so both read the location only after this has run.
 *
 * Next.js keeps its own copy of the URL: a client-side navigation still shows
 * the token through `useSearchParams`. A full load or back/forward does not,
 * since Next reads `window.location` then. So every reader goes through
 * `guestTokenFromUrl`, which falls back to the hand-off below.
 *
 * The hand-off is keyed by ORDER ID — the `orderId` query param, or the id in
 * an `/order/<id>/...` route — and is only ever read for the order the URL in
 * front of the reader names. That keeps the old meaning exactly: a URL that
 * names order 5 and once carried 5's token still has it on reload, and no URL
 * can be paired with another order's token. It lives in sessionStorage
 * (survives a reload, scoped to the tab, gone when the tab closes) and on
 * `window`, for a browser that refuses storage.
 *
 * A token in a URL that names no order is still stripped, and not kept: half a
 * session is something every reader already refuses.
 *
 * The script is a STRING for the reasons given in credit-url-guard.ts — what
 * ships is exactly what the tests evaluate.
 */

/** sessionStorage key of the hand-off: `{ [orderId]: token }`. */
export const GUEST_TOKEN_HANDOFF_KEY = "guest_session_handoff_v1";

/** The same map, as a property on `window`. */
export const GUEST_TOKEN_HANDOFF_GLOBAL = "__fdGuestSessionTokens";

/** Set once the history methods are wrapped, so a second run does not re-wrap. */
const GUARD_INSTALLED_GLOBAL = "__fdGuestUrlGuard";

/** Both spellings: `guestToken` is the legacy one, still read as a fallback. */
const TOKEN_PARAMS = ["guest_session_token", "guestToken"] as const;

/**
 * Self-contained, dependency-free, safe to inline into a `<script>` tag, and
 * idempotent: run twice, it neither wraps history twice nor finds anything
 * left to strip.
 */
export const GUEST_TOKEN_URL_GUARD_SCRIPT = `(function(){try{
var w=window,h=w.history,N=['${TOKEN_PARAMS[0]}','${TOKEN_PARAMS[1]}'];
function keep(id,t){var m=w['${GUEST_TOKEN_HANDOFF_GLOBAL}']||(w['${GUEST_TOKEN_HANDOFF_GLOBAL}']={});m[id]=t;
try{var s=JSON.parse(w.sessionStorage.getItem('${GUEST_TOKEN_HANDOFF_KEY}')||'{}')||{};s[id]=t;w.sessionStorage.setItem('${GUEST_TOKEN_HANDOFF_KEY}',JSON.stringify(s));}catch(e){}}
function clean(url){var u=new URL(String(url),w.location.href);if(u.origin!==w.location.origin)return null;
var p=u.searchParams;if(!p.has(N[0])&&!p.has(N[1]))return null;
var t=p.get(N[0])||p.get(N[1]);var r=/(?:^|\\/)order\\/([^/?#]+)/.exec(u.pathname);
var id=p.get('orderId')||(r?decodeURIComponent(r[1]):'');if(t&&id)keep(id,t);
p.delete(N[0]);p.delete(N[1]);var q=p.toString();return u.pathname+(q?'?'+q:'')+u.hash;}
if(!w['${GUARD_INSTALLED_GLOBAL}']){w['${GUARD_INSTALLED_GLOBAL}']=1;
['pushState','replaceState'].forEach(function(k){var o=h[k];h[k]=function(s,x,url){
if(url!=null){try{var c=clean(url);if(c!==null)url=c;}catch(e){}}return o.call(h,s,x,url);};});}
var c=clean(w.location.href);if(c!==null)h.replaceState(h.state,'',c);
}catch(e){}})();`;

function handoffFromWindow(): Record<string, string> | null {
  const value = (window as unknown as Record<string, unknown>)[
    GUEST_TOKEN_HANDOFF_GLOBAL
  ];
  return value && typeof value === "object"
    ? (value as Record<string, string>)
    : null;
}

function handoffFromStorage(): Record<string, string> | null {
  try {
    const raw = window.sessionStorage.getItem(GUEST_TOKEN_HANDOFF_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** The token the guard took out of a URL naming `orderId`, or null. */
export function readGuestTokenHandoff(
  orderId: string | null | undefined,
): string | null {
  if (!orderId || typeof window === "undefined") return null;
  const token =
    handoffFromWindow()?.[orderId] || handoffFromStorage()?.[orderId];
  return typeof token === "string" && token ? token : null;
}

/**
 * Drop `orderId`'s token from the hand-off, so a session the shop has decided
 * to throw away cannot be re-adopted from it on the next load.
 *
 * @returns true if there was one to drop.
 */
export function forgetGuestTokenHandoff(
  orderId: string | null | undefined,
): boolean {
  if (!orderId || typeof window === "undefined") return false;
  let forgot = false;
  const onWindow = handoffFromWindow();
  if (onWindow && orderId in onWindow) {
    delete onWindow[orderId];
    forgot = true;
  }
  const stored = handoffFromStorage();
  if (stored && orderId in stored) {
    delete stored[orderId];
    forgot = true;
    try {
      window.sessionStorage.setItem(
        GUEST_TOKEN_HANDOFF_KEY,
        JSON.stringify(stored),
      );
    } catch {
      // Storage refused the write; the window copy is gone either way.
    }
  }
  return forgot;
}

/**
 * The guest token "in the URL" for `orderId`, wherever the guard has put it.
 *
 * In order: the params the caller already holds (Next's `useSearchParams`,
 * which keeps the token after a client-side navigation), the live address bar
 * (a page the guard has not run on — tests, or a script error), then the
 * hand-off for the order this URL names. Either spelling.
 *
 * @param orderId the order the URL in front of the caller names. Without one
 *                the hand-off is never consulted: it must not pair a token
 *                with an order it was not given for.
 */
export function guestTokenFromUrl(
  orderId: string | null | undefined,
  params?: { get(name: string): string | null } | null,
): string | null {
  for (const name of TOKEN_PARAMS) {
    const fromParams = params?.get(name);
    if (fromParams) return fromParams;
  }
  if (typeof window === "undefined") return null;
  const live = new URLSearchParams(window.location.search);
  for (const name of TOKEN_PARAMS) {
    const fromLive = live.get(name);
    if (fromLive) return fromLive;
  }
  return readGuestTokenHandoff(orderId);
}

/**
 * The order the current URL names, the way the guard reads it: the `orderId`
 * query param, else the id in an `/order/<id>/...` route. Null when it names
 * none.
 */
export function orderIdNamedByUrl(): string | null {
  if (typeof window === "undefined") return null;
  const fromParam = new URLSearchParams(window.location.search).get("orderId");
  if (fromParam) return fromParam;
  const route = /(?:^|\/)order\/([^/?#]+)/.exec(window.location.pathname);
  return route ? decodeURIComponent(route[1]) : null;
}

/**
 * The token to put into links for `activeOrderId`, or null.
 *
 * Before the guard, the header copied the token into its logo, home and
 * calendar links only while the address bar carried it. Those hrefs sit in the
 * DOM, where Clarity's recording and Meta's automatic click events read them,
 * so they must not start carrying it on every page just because some earlier
 * URL in the tab did: the token is only given out on a URL that names the
 * active order, exactly as the hand-off is only read for the order its URL
 * names.
 */
export function guestTokenForActiveOrder(
  activeOrderId: string | null | undefined,
): string | null {
  if (!activeOrderId) return null;
  if (orderIdNamedByUrl() !== activeOrderId) return null;
  return guestTokenFromUrl(activeOrderId);
}
